import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  type WebsiteConfig,
  websiteConfigStrictSchema,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/ErrorAlert';
import { HtmlEditor } from '@/components/HtmlEditor';
import { UploadButton } from '@/components/UploadButton';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { NullableInput } from '@/components/ui+/NullableInput';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

export const Route = createFileRoute('/r/$restaurantId/s/website-config')({
  component: Component,
});

function Component() {
  const { restaurantId } = Route.useParams();
  const convex = useConvex();
  const {
    data: websiteConfig,
    error,
    isError,
    isPending,
  } = useQuery(api.websiteConfigs.getByRestaurantId, { restaurantId });

  const form = useForm({
    resolver: zodResolver(websiteConfigStrictSchema),
  });

  // Subscribe to changes
  useEffect(() => {
    if (!websiteConfig) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(websiteConfig, { keepDirty: true, keepDirtyValues: true });
  }, [websiteConfig]);

  const onValidSubmit = async (formValues: WebsiteConfig) => {
    try {
      await convex.mutation(api.websiteConfigs.save, {
        restaurantId,
        websiteConfig: formValues,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  if (isPending) return <LoadingIcon />;

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading mobile app config" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Website Config</CardTitle>
        <CardDescription>Custom website with online ordering</CardDescription>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            id="form"
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
            className="grid gap-6"
          >
            {/* Domain name */}
            <FormField
              control={form.control}
              name="domainName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Domain Name</FormLabel>
                  <FormControl>
                    <NullableInput {...field} placeholder={'E.g. myrestaurant.com'} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Facebook URL */}
            <FormField
              control={form.control}
              name="facebookUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facebook URL</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Instagram URL */}
            <FormField
              control={form.control}
              name="instagramUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instagram URL</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* About us */}
            <FormField
              control={form.control}
              name="aboutUsContent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>About us</FormLabel>
                  <FormControl>
                    <HtmlEditor onChange={field.onChange} value={field.value ?? null} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Images (can only be added once initial config is set up) */}
            {!!websiteConfig?._id && (
              <>
                <div className={'flex gap-4 justify-end'}>
                  {!websiteConfig.bannerImageId ? (
                    <UploadButton
                      buttonText="Upload banner image"
                      fileTypes={['.jpg']}
                      imageHeight={270}
                      imageWidth={970}
                      onUploadComplete={async (uploadResponse) => {
                        const storageId = uploadResponse[0]?.response.storageId;
                        if (!storageId) return;
                        await convex.mutation(api.websiteConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'banner',
                        });
                        toast.success('Banner uploaded successfully');
                      }}
                      onUploadError={(error: unknown) => {
                        console.error('upload error', error);
                        toast.error('Error uploading file!');
                      }}
                      uploadUrl={() => convex.mutation(api.files.generateUploadUrl)}
                    />
                  ) : (
                    <>
                      <div className="flex flex-col w-full">
                        {websiteConfig.bannerImageId && (
                          <div className={'text-muted-foreground text-sm'}>
                            {websiteConfig.bannerImageId}
                          </div>
                        )}
                        {websiteConfig.bannerImageUrl && (
                          <a
                            className={
                              'text-blue-600 hover:text-blue-800 underline hover:no-underline text-sm'
                            }
                            href={websiteConfig.bannerImageUrl}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {websiteConfig.bannerImageUrl}
                          </a>
                        )}
                      </div>
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await convex.mutation(api.websiteConfigs.deleteImage, {
                            restaurantId,
                            type: 'banner',
                          });
                        }}
                        variant={'destructive'}
                      >
                        Delete Banner
                      </ActionButton>
                    </>
                  )}
                </div>

                {/* About us image */}
                <div className={'flex gap-4 justify-end'}>
                  {!websiteConfig.aboutUsImageId ? (
                    <UploadButton
                      buttonText="Upload about us image"
                      fileTypes={['.jpg']}
                      // imageHeight={270}
                      // imageWidth={970}
                      onUploadComplete={async (uploadResponse) => {
                        const storageId = uploadResponse[0]?.response.storageId;
                        if (!storageId) return;
                        await convex.mutation(api.websiteConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'aboutUs',
                        });
                        toast.success('About us uploaded successfully');
                      }}
                      onUploadError={(error: unknown) => {
                        console.error('upload error', error);
                        toast.error('Error uploading file!');
                      }}
                      uploadUrl={() => convex.mutation(api.files.generateUploadUrl)}
                    />
                  ) : (
                    <>
                      <div className="flex flex-col w-full">
                        {websiteConfig.aboutUsImageId && (
                          <div className={'text-muted-foreground text-sm'}>
                            {websiteConfig.aboutUsImageId}
                          </div>
                        )}
                        {websiteConfig.aboutUsImageUrl && (
                          <a
                            className={
                              'text-blue-600 hover:text-blue-800 underline hover:no-underline text-sm'
                            }
                            href={websiteConfig.aboutUsImageUrl}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {websiteConfig.aboutUsImageUrl}
                          </a>
                        )}
                      </div>
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await convex.mutation(api.websiteConfigs.deleteImage, {
                            restaurantId,
                            type: 'aboutUs',
                          });
                        }}
                        variant={'destructive'}
                      >
                        Delete About Us
                      </ActionButton>
                    </>
                  )}
                </div>
              </>
            )}
          </form>
        </FormWrapper>
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={form.formState.isSubmitting || !form.formState.isDirty}
          loading={form.formState.isSubmitting}
          type="submit"
          form="form"
        >
          Save
        </ActionButton>
      </CardFooter>
    </Card>
  );
}
